import { apiClient } from '@/lib/api-client';

export interface WebhookConfig {
  id: string;
  webhookUrl: string;
  webhookSecretMasked: string;
  webhookSecret?: string;
  platform?: string;
  isActive: boolean;
  createdAt: string;
}

export interface WebhookFieldMapping {
  id: string;
  sourceField: string;
  targetField: string;
  isAutoMapped: boolean;
  confidence?: number;
  createdAt: string;
  updatedAt: string;
}

export interface WebhookDeliveryLog {
  id: string;
  webhookConfigId: string;
  status: 'SUCCESS' | 'AUTH_FAILED' | 'MAPPING_ERROR' | 'VALIDATION_FAILED' | 'ERROR';
  rawPayload: Record<string, any>;
  mappedData?: Record<string, any>;
  errorMessage?: string;
  leadId?: string;
  ipAddress?: string;
  createdAt: string;
}

export interface DeliveryLogsResponse {
  logs: WebhookDeliveryLog[];
  total: number;
  page: number;
  limit: number;
}

export const webhookApi = {
  getConfig: async (): Promise<WebhookConfig | null> => {
    return apiClient.get('/webhook/config');
  },

  createConfig: async (data: { platform?: string }): Promise<WebhookConfig> => {
    return apiClient.post('/webhook/config', data);
  },

  updateConfig: async (data: {
    platform?: string;
    isActive?: boolean;
  }): Promise<WebhookConfig> => {
    return apiClient.put('/webhook/config', data);
  },

  regenerateSecret: async (): Promise<WebhookConfig> => {
    return apiClient.post('/webhook/config/regenerate-secret', {});
  },

  getFieldMappings: async (): Promise<WebhookFieldMapping[]> => {
    return apiClient.get('/webhook/config/field-mappings');
  },

  updateFieldMappings: async (
    mappings: Array<{ sourceField: string; targetField: string }>,
  ): Promise<WebhookFieldMapping[]> => {
    return apiClient.put('/webhook/config/field-mappings', { mappings });
  },

  deleteFieldMapping: async (mappingId: string): Promise<void> => {
    return apiClient.delete(`/webhook/config/field-mappings/${mappingId}`);
  },

  refreshFieldMappings: async (): Promise<{
    scannedDeliveries: number;
    discoveredFields: number;
    newlyMappedFields: string[];
    mappings: WebhookFieldMapping[];
  }> => {
    return apiClient.post('/webhook/config/field-mappings/refresh', {});
  },

  getDeliveryLogs: async (
    page = 1,
    limit = 50,
  ): Promise<DeliveryLogsResponse> => {
    return apiClient.get(`/webhook/deliveries?page=${page}&limit=${limit}`);
  },

  testWebhook: async (payload: Record<string, any>): Promise<{
    platform: string;
    normalizedFields: Record<string, string>;
    mappings: Record<string, string>;
    leadPreview: Record<string, any>;
  }> => {
    return apiClient.post('/webhook/test', payload);
  },
};
